import { responseError } from "../lib/response-error";
import type { UserConfig } from "../lib/feature-flags";
export type { UserConfig, FeatureFlagDefinition } from "../lib/feature-flags";
// All API calls go through local backend proxy which handles authentication and API tokens
import { PROMPT_SERVICE_CONFIG } from "../config/api.config";
import type { ChatHistoryOrder } from "../lib/session-time";
import type { ClassroomDeletionImpact } from "../lib/classroom-deletion";

export interface Teacher {
  teacher_id: number;
  user_id: number;
  organization_id?: string | null;
  account_role?: string;
  name: string;
  username?: string | null;
  email: string | null;
  max_usage_allowed_ms: number;
}

export interface TeachersFilterPage {
  items: Teacher[];
  total: number;
  limit: number;
  offset: number;
}

export interface ReassignLessonResponse {
  success: true;
  noop: boolean;
  teacher_name: string | null;
  cross_org: boolean;
  pruned: {
    grants: number;
    classrooms: number;
    folders: number;
    spaces: number;
  };
}

export const CROSS_ORG_CONFIRM = "cross_org_confirm";

/** The server refused a flag update (4xx) before writing anything. */
export class ReassignError extends Error {
  readonly code?: string;
  readonly sourceOrg?: string | null;
  readonly targetOrg?: string | null;

  constructor(
    message: string,
    code?: string,
    sourceOrg?: string | null,
    targetOrg?: string | null,
  ) {
    super(message);
    this.name = "ReassignError";
    this.code = code;
    this.sourceOrg = sourceOrg;
    this.targetOrg = targetOrg;
  }

  get needsCrossOrgConfirm(): boolean {
    return this.code === CROSS_ORG_CONFIRM;
  }
}

export interface Student {
  student_id: number;
  user_id: number;
  organization_id?: string | null;
  account_role?: string;
  name: string;
  email: string;
}

export interface MessageContent {
  type: string;
  text: string;
}

export interface Message {
  role: "assistant" | "user";
  content: MessageContent[];
}

/** One line of a notepad bite's conversation. The stamps are absent on lines stored before the gateway wrote them. */
export interface NotepadMessage {
  role: "user" | "assistant";
  /** Assistant lines keep the tutor's canvas commands inline as <op>{json}</op>. */
  content: string;
  /** The turn id; it also names the turn's audio clip in the Audio Recordings tab. */
  turn_id?: string | null;
  /** Epoch milliseconds. */
  ts_ms?: number | null;
  /** User lines: "voice", "typed", or "frontend" (the app's own prompt). */
  source?: string | null;
}

export interface Session {
  session_key: string;
  messages: Message[];
  created_at?: string; // ISO timestamp from backend
  lesson_id?: string | null;
  lesson_name?: string | null;
  slide_index?: number | null;
  /** INSTRUCTIONAL or PRACTICE on a notepad bite. */
  bite_type?: string | null;
  /** A notepad bite's conversation; null on a classic slide. A notepad bite's `messages` hold only the system prompt. */
  notepad_messages?: NotepadMessage[] | null;
  /** True when the conversation is in S3 and the studio could not read it. */
  notepad_messages_unavailable?: boolean;
  /** True when the conversation is in S3 and the page's read budget ran out first; fetch it with getNotepadHistory. */
  notepad_messages_deferred?: boolean;
  /** Names a deferred conversation for getNotepadHistory; set only when deferred. */
  notepad_messages_s3_key?: string | null;
}

/** One notepad bite's conversation, read alone. */
export interface NotepadHistory {
  notepad_messages: NotepadMessage[] | null;
  notepad_messages_unavailable: boolean;
}

export interface ChatHistory {
  user_id: string;
  total_sessions: number;
  sessions: Session[];
  next_token?: string;
  /** The time the sessions are ordered and date-filtered by. "started" also when "saved" was asked for and the studio could not order by it yet. */
  order_by?: ChatHistoryOrder;
}

export interface Lesson {
  lesson_id: number;
  name: string;
  teacher_name: string | null;
  is_published: boolean;
  slide_count?: number;
}

export interface LessonsPage {
  items: Lesson[];
  total: number;
  limit: number;
  offset: number;
}

export interface LessonStandard {
  standard_id: number;
  code: string;
  description: string;
}

export interface ChatHistoryParams {
  lesson_id?: string;
  slide_index?: string;
  start_date?: string; // "YYYY-MM-DD" (a Pacific day) or an ISO instant
  end_date?: string;
  limit?: number;
  next_token?: string;
  /** What the list is ordered, and start_date/end_date applied, by. The studio default is "saved". */
  order_by?: ChatHistoryOrder;
}

// Prompt Service Types
export interface Placeholder {
  name: string;
  description: string;
  required: boolean;
  default_value?: string;
  prompt_id?: string | null;
}

export interface PromptVersion {
  version: string;
  content: string;
  status: "draft" | "active" | "archived";
  changelog: string;
  created_at: string;
  created_by: string;
  parent_version?: string;
  content_hash: string;
}

/** A prompt version without its content, as the Experiments tab reads it. */
export type PromptVersionStatus = Pick<PromptVersion, "version" | "status">;

export interface Prompt {
  application_id: string;
  prompt_id: string;
  name: string;
  description: string;
  category: "system" | "user" | "assistant" | "tool";
  org?: string;
  placeholders: Placeholder[];
  tags: string[];
  hidden?: boolean;
  latest_version: string;
  active_version: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface PromptCreateRequest {
  prompt_id: string;
  application_id: string;
  name: string;
  description: string;
  category: string;
  org?: string;
  content: string;
  placeholders?: Placeholder[];
  tags?: string[];
}

export interface VersionCreateRequest {
  content: string;
  changelog: string;
  created_by?: string;
}

export interface ExperimentVariant {
  variant_id: string;
  name: string;
  description: string;
  prompt_version: string;
  weight: number;
}

export interface Experiment {
  experiment_id: string;
  application_id: string;
  prompt_id: string;
  name: string;
  description: string;
  hypothesis: string;
  status: "draft" | "running" | "paused" | "completed";
  variants: ExperimentVariant[];
  start_date?: string;
  end_date?: string;
  total_assignments: number;
  created_at: string;
  created_by: string;
}

export interface ExperimentCreateRequest {
  application_id: string;
  prompt_id: string;
  name: string;
  description: string;
  hypothesis: string;
  variants: Omit<ExperimentVariant, "variant_id">[];
}

// Tutor Version Assignment Types
export interface TutorVersionAssignment {
  lesson_id: string;
  tutor_version_id: string;
  application_id: string;
  assigned_by: string;
  assigned_at: string;
}

// Tutor Version Types
export interface SlotOption {
  prompt_id?: string;
  version?: number;
  default?: boolean;
  runtime_variable?: boolean; // when true, slot is filled at runtime by the consumer
}

export interface SlotAssignment {
  placeholder_name: string;
  options: SlotOption[];
}

export interface PromptSnapshot {
  prompt_id: string;
  version: number;
  model?: string;
  llm_config_name?: string;
  slot_assignments?: SlotAssignment[];
}

export interface NamedLLMConfig {
  name: string;
  config: LLMConfig;
}

export interface LLMConfig {
  // Primary tier — unchanged for backward compatibility.
  provider: string;
  model: string;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  // Fallback tiers (tier 2, 3, …) in priority order. `model` is a litellm
  // model string; the runtime swaps it into the same request.
  fallbacks?: { provider: string; model: string }[];
  [key: string]: unknown;
}

export interface STTConfig {
  // Primary tier — unchanged for backward compatibility.
  provider: string;
  model: string;
  language?: string;
  // Fallback tiers (tier 2, 3, …) in priority order; each carries its own model.
  fallbacks?: { provider: string; model: string }[];
}

export interface TTSConfig {
  provider: string;
  model: string;
  voice?: string;
}

export interface TutorVersion {
  tutor_version_id: string;
  application_id: string;
  name: string;
  description: string;
  org?: string;
  prompts: PromptSnapshot[];
  llm_config: LLMConfig;
  llm_configs?: NamedLLMConfig[];
  stt_config?: STTConfig;
  tts_config?: TTSConfig;
  is_default?: boolean;
  status: "draft" | "published" | "archived" | "dev";
  version_number: number;
  created_at: string;
  created_by: string;
}

export interface ApplicationTVSection {
  application_id: string;
  prompts: PromptSnapshot[];
  llm_config: LLMConfig;
  llm_configs?: NamedLLMConfig[];
}

export interface TutorVersionCreateRequest {
  application_id: string;
  name: string;
  description?: string;
  org?: string;
  prompts: PromptSnapshot[];
  llm_config: LLMConfig;
  llm_configs?: NamedLLMConfig[];
  stt_config?: STTConfig;
  tts_config?: TTSConfig;
  created_by?: string;
  applications?: ApplicationTVSection[];
}

export interface ResolvedSlotOption {
  prompt_id?: string;
  version?: number;
  content?: string;
  default?: boolean;
  runtime_variable?: boolean;
}

export interface ResolvedSlotAssignment {
  placeholder_name: string;
  options: ResolvedSlotOption[];
}

export interface ResolvedPrompt {
  prompt_id: string;
  version: number;
  content: string;
  slot_assignments?: ResolvedSlotAssignment[];
}

export interface ResolvedTutorVersion {
  tutor_version_id: string;
  prompts: ResolvedPrompt[];
}

/** A tutor-version export file: the body of prompt-config's batch import. */
export interface TutorVersionBundle {
  name: string;
  description: string;
  created_by?: string;
  applications: Record<string, unknown>[];
}

const fetchOptions: RequestInit = {
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};

const TEACHERS_CACHE_TTL_MS = 5 * 60 * 1000;
let teachersForFilterCache:
  | { data: Teacher[]; expiresAt: number }
  | null = null;

async function readApiError(
  response: Response,
  fallback: string,
): Promise<string> {
  const suffix = `${response.status} ${response.statusText}`.trim();

  try {
    const text = await response.text();
    if (!text) return suffix ? `${fallback} (${suffix})` : fallback;

    try {
      const payload = JSON.parse(text) as {
        error?: string;
        detail?: string | Array<{ loc?: unknown[]; msg?: string }>;
        message?: string;
        summary?: string;
      };
      if (payload?.error) return payload.error;
      // FastAPI HTTPException -> detail is a string.
      if (typeof payload?.detail === "string") return payload.detail;
      // Pydantic 422 -> detail is an array of {loc, msg}. Surface field + message
      // (e.g. "concurrency: Input should be greater than or equal to 1").
      if (Array.isArray(payload?.detail)) {
        const parts = payload.detail
          .map((e) => {
            const field = Array.isArray(e?.loc) ? e.loc.at(-1) : undefined;
            return field ? `${field}: ${e?.msg}` : e?.msg;
          })
          .filter(Boolean);
        if (parts.length) return parts.join("; ");
      }
      if (payload?.message) return payload.message;
      if (payload?.summary) return payload.summary;
    } catch {
      return suffix ? `${fallback} (${suffix}): ${text}` : `${fallback}: ${text}`;
    }
  } catch {
    // Ignore malformed or empty error bodies and fall back to the default.
  }

  return suffix ? `${fallback} (${suffix})` : fallback;
}

async function readJsonIfPresent<T>(response: Response): Promise<T | null> {
  const text = await response.text();
  if (!text) return null;
  return JSON.parse(text) as T;
}

function normalizeTtsPoolAccountsResponse(payload: unknown): TtsPoolAccount[] {
  if (Array.isArray(payload)) {
    return payload as TtsPoolAccount[];
  }

  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (Array.isArray(record.accounts)) {
      return record.accounts as TtsPoolAccount[];
    }
    if (Array.isArray(record.items)) {
      return record.items as TtsPoolAccount[];
    }
  }

  throw new Error("Invalid TTS pool accounts response");
}

function normalizeTtsPoolAccountResponse(payload: unknown): TtsPoolAccount {
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (record.account && typeof record.account === "object") {
      return record.account as TtsPoolAccount;
    }
    if (typeof record.account_id === "string") {
      return record as TtsPoolAccount;
    }
  }

  throw new Error("Invalid TTS pool account response");
}

function normalizeTtsPoolMetricsResponse(payload: unknown): TtsPoolMetrics {
  if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (record.summary && typeof record.summary === "object") {
      const summary = {
        ...(record.summary as Record<string, unknown>),
      } as TtsPoolMetrics;
      if (
        (summary.environment == null || summary.environment === "") &&
        typeof record.stage === "string"
      ) {
        summary.environment = record.stage;
      }
      return summary;
    }
    return record as TtsPoolMetrics;
  }

  throw new Error("Invalid TTS pool metrics response");
}

export const apiService = {
  async getTeachers(organizationId?: string): Promise<Teacher[]> {
    const params = new URLSearchParams();
    if (organizationId) params.set("organization_id", organizationId);
    const response = await fetch(`/api/proxy/teachers${params.size ? `?${params}` : ""}`, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch teachers");
    return response.json();
  },

  async getStudents(organizationId?: string): Promise<Student[]> {
    const params = new URLSearchParams();
    if (organizationId) params.set("organization_id", organizationId);
    const response = await fetch(`/api/proxy/students${params.size ? `?${params}` : ""}`, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch students");
    return response.json();
  },

  async getChatHistory(
    userId: string | number,
    params?: ChatHistoryParams,
  ): Promise<ChatHistory> {
    const queryParams = new URLSearchParams();
    if (params?.lesson_id) queryParams.append("lesson_id", params.lesson_id);
    if (params?.slide_index)
      queryParams.append("slide_index", params.slide_index);
    if (params?.start_date) queryParams.append("start_date", params.start_date);
    if (params?.end_date) queryParams.append("end_date", params.end_date);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.next_token) queryParams.append("next_token", params.next_token);
    if (params?.order_by) queryParams.append("order_by", params.order_by);

    const queryString = queryParams.toString();
    const url = `/api/proxy/chat-history/${userId}${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch chat history");
    return response.json();
  },

  /** A notepad conversation the chat-history page deferred (notepad_messages_s3_key). */
  async getNotepadHistory(userId: string | number, key: string): Promise<NotepadHistory> {
    const url = `/api/proxy/chat-history/${encodeURIComponent(String(userId))}/notepad-history?${new URLSearchParams({ key })}`;
    const response = await fetch(url, fetchOptions);
    if (!response.ok) throw new Error("Failed to fetch notepad history");
    return response.json();
  },

  async updateTeacherUsageLimit(
    teacherId: number,
    limitMs: number,
  ): Promise<void> {
    const response = await fetch(
      `/api/proxy/teacher/${teacherId}/usage-limit`,
      {
        ...fetchOptions,
        method: "PATCH",
        body: JSON.stringify({ max_usage_allowed_ms: limitMs }),
      },
    );
    if (!response.ok) throw await responseError(response, "Failed to update usage limit");
  },

  async getLessons(search?: string, userId?: string, filters?: { organizationId?: string; teacherId?: number; status?: "published" | "draft" }): Promise<Lesson[]> {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (userId) params.append("user_id", userId);
    if (filters?.organizationId) params.append("organization_id", filters.organizationId);
    if (filters?.teacherId) params.append("teacher_id", String(filters.teacherId));
    if (filters?.status) params.append("status", filters.status);

    const queryString = params.toString();
    const url = `/api/proxy/lessons${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch lessons");
    return response.json();
  },

  async getLessonsPage(params?: {
    search?: string;
    teacherId?: number;
    organizationId?: string;
    status?: "published" | "draft";
    limit?: number;
    offset?: number;
  }): Promise<LessonsPage> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.teacherId != null) {
      queryParams.append("teacher_id", String(params.teacherId));
    }
    if (params?.organizationId) queryParams.append("organization_id", params.organizationId);
    if (params?.status) queryParams.append("status", params.status);
    queryParams.append("limit", String(params?.limit ?? 50));
    queryParams.append("offset", String(params?.offset ?? 0));

    const response = await fetch(
      `/api/proxy/lessons?${queryParams.toString()}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch lessons");
    return response.json();
  },

  /**
   * The lesson with exactly this ID, drafts included, or null. The search also
   * matches names, so the exact ID is picked out of the page.
   */
  async findLessonById(lessonId: number): Promise<Lesson | null> {
    const page = await this.getLessonsPage({ search: String(lessonId), limit: 10 });
    return page.items.find((lesson) => lesson.lesson_id === lessonId) ?? null;
  },

  // Lesson Config API
  async getLessonSlides(lessonId: number): Promise<LessonDetail> {
    const response = await fetch(
      `/api/proxy/lessons/${lessonId}/slides`,
      fetchOptions,
    );
    if (!response.ok) {
      let detail = "Failed to fetch lesson slides";
      try {
        const err = await response.json();
        if (err?.detail) detail = err.detail;
      } catch { /* ignore */ }
      throw new Error(detail);
    }
    return response.json();
  },

  async updateSlideMetadata(
    lessonId: number,
    slideId: number,
    metadata: Record<string, unknown>,
  ): Promise<SlideMetadataUpdateResponse> {
    const response = await fetch(
      `/api/proxy/lessons/${lessonId}/slides/${slideId}/metadata`,
      {
        ...fetchOptions,
        method: "PATCH",
        body: JSON.stringify({ metadata }),
      },
    );
    if (!response.ok) {
      let detail = "Failed to update slide metadata";
      try {
        const err = await response.json();
        if (err?.detail || err?.error) detail = err.detail || err.error;
      } catch { /* ignore */ }
      throw new Error(detail);
    }
    return response.json();
  },

  async updateSlidesMetadata(
    lessonId: number,
    slideIds: number[],
    metadata: Record<string, unknown>,
  ): Promise<SlideMetadataBulkUpdateResponse> {
    const response = await fetch(
      `/api/proxy/lessons/${lessonId}/slides/metadata`,
      {
        ...fetchOptions,
        method: "PATCH",
        body: JSON.stringify({ slide_ids: slideIds, metadata }),
      },
    );
    if (!response.ok) {
      let detail = "Failed to update slide metadata";
      try {
        const err = await response.json();
        if (err?.detail || err?.error) detail = err.detail || err.error;
      } catch { /* ignore */ }
      throw new Error(detail);
    }
    return response.json();
  },

  // Tutor Version Assignment API
  // These four go through the dashboard server (tab-guarded), not straight to
  // the prompt service: the Lessons tab is grantable, and a browser call to
  // the service would carry no dashboard session.
  async getAssignment(appId: string, lessonId: string): Promise<TutorVersionAssignment | null> {
    const response = await fetch(
      `/api/proxy/prompt-config/assignments/${appId}/${lessonId}`,
      fetchOptions,
    );
    if (response.status === 404) return null;
    if (!response.ok) throw await responseError(response, "Failed to fetch assignment");
    return response.json();
  },

  async setAssignment(
    appId: string,
    lessonId: string,
    tutorVersionId: string,
    assignedBy: string,
  ): Promise<TutorVersionAssignment> {
    // assigned_by is set server-side from the session; the argument is kept
    // so callers need no change.
    void assignedBy;
    const response = await fetch(
      `/api/proxy/prompt-config/assignments/${appId}/${lessonId}`,
      {
        ...fetchOptions,
        method: "PUT",
        body: JSON.stringify({ tutor_version_id: tutorVersionId }),
      },
    );
    if (!response.ok) throw await responseError(response, "Failed to set assignment");
    return response.json();
  },

  async removeAssignment(appId: string, lessonId: string): Promise<void> {
    const response = await fetch(
      `/api/proxy/prompt-config/assignments/${appId}/${lessonId}`,
      { ...fetchOptions, method: "DELETE" },
    );
    if (!response.ok) throw await responseError(response, "Failed to remove assignment");
  },

  async duplicateLesson(
    lessonId: number,
    teacherId?: number,
  ): Promise<{
    success: boolean;
    new_lesson_id: number;
    lesson_name: string;
    is_published: boolean;
  }> {
    const response = await fetch(
      `/api/proxy/lessons/${lessonId}/duplicate`,
      {
        ...fetchOptions,
        method: "POST",
        body: JSON.stringify(teacherId != null ? { teacher_id: teacherId } : {}),
      },
    );
    if (!response.ok) {
      let detail = "Failed to duplicate lesson";
      try {
        const err = await response.json();
        if (err?.detail || err?.error) detail = err.detail || err.error;
      } catch { /* ignore */ }
      throw new Error(detail);
    }
    return response.json();
  },

  async deleteLesson(lessonId: number): Promise<void> {
    const response = await fetch(
      `/api/proxy/lessons/${lessonId}/delete`,
      {
        ...fetchOptions,
        method: "DELETE",
      },
    );
    if (!response.ok) {
      throw new Error(await readApiError(response, "Failed to delete lesson"));
    }
  },

  async reassignLesson(
    lessonId: number,
    teacherId: number,
    confirmCrossOrg = false,
  ): Promise<ReassignLessonResponse> {
    const response = await fetch(
      `/api/proxy/lessons/${lessonId}/reassign`,
      {
        ...fetchOptions,
        method: "PATCH",
        body: JSON.stringify({
          teacher_id: teacherId,
          confirm_cross_org: confirmCrossOrg,
        }),
      },
    );
    if (!response.ok) {
      let detail = "Failed to reassign lesson";
      let code: string | undefined;
      let sourceOrg: string | null | undefined;
      let targetOrg: string | null | undefined;
      try {
        const err = await response.json();
        if (err?.detail || err?.error) detail = err.detail || err.error;
        code = err?.code;
        sourceOrg = err?.source_org;
        targetOrg = err?.target_org;
      } catch { /* ignore */ }
      throw new ReassignError(detail, code, sourceOrg, targetOrg);
    }
    teachersForFilterCache = null;
    return response.json();
  },

  async grantLesson(
    lessonId: number,
    teacherId: number,
  ): Promise<{ success: boolean; created: boolean; teacher_name: string | null }> {
    const response = await fetch(
      `/api/proxy/lessons/${lessonId}/grant`,
      {
        ...fetchOptions,
        method: "POST",
        body: JSON.stringify({ teacher_id: teacherId }),
      },
    );
    if (!response.ok) throw await responseError(response, "Failed to grant lesson");
    return response.json();
  },

  async exportLesson(lessonId: number): Promise<LessonExport> {
    const response = await fetch(
      `/api/proxy/lessons/${lessonId}/export`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to export lesson");
    return response.json();
  },

  async getLessonStandards(lessonId: number): Promise<LessonStandard[]> {
    const response = await fetch(`/api/proxy/lessons/${lessonId}/standards`, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch lesson standards");
    return response.json();
  },

  async addLessonStandard(lessonId: number, standardId: number): Promise<void> {
    const response = await fetch(`/api/proxy/lessons/${lessonId}/standards/${standardId}`, {
      ...fetchOptions,
      method: "POST",
    });
    if (!response.ok) throw await responseError(response, "Failed to add lesson standard");
  },

  async removeLessonStandard(lessonId: number, standardId: number): Promise<void> {
    const response = await fetch(`/api/proxy/lessons/${lessonId}/standards/${standardId}`, {
      ...fetchOptions,
      method: "DELETE",
    });
    if (!response.ok) throw await responseError(response, "Failed to remove lesson standard");
  },

  async getStandardFrameworks(): Promise<string[]> {
    const response = await fetch("/api/proxy/standards/frameworks", fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch standard frameworks");
    const data = await response.json();
    return data.frameworks;
  },

  async searchStandards(query: string, framework: string): Promise<LessonStandard[]> {
    const params = new URLSearchParams({ q: query, framework });
    const response = await fetch(`/api/proxy/standards/search?${params}`, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to search standards");
    return response.json();
  },

  // Audio Recordings API
  async getAudioUsers(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{ users: AudioUser[] }> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append("start_date", params.start_date);
    if (params?.end_date) queryParams.append("end_date", params.end_date);

    const queryString = queryParams.toString();
    const url = `/api/proxy/audio-recordings/users${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch audio users");
    return response.json();
  },

  async getUserSessions(
    userId: string,
    params?: {
      start_date?: string;
      end_date?: string;
      limit?: number;
      before?: string;
    },
  ): Promise<{
    user_id: string;
    sessions: AudioSession[];
    total_sessions?: number;
    next_cursor?: string | null;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append("start_date", params.start_date);
    if (params?.end_date) queryParams.append("end_date", params.end_date);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.before) queryParams.append("before", params.before);

    const queryString = queryParams.toString();
    const url = `/api/proxy/audio-recordings/users/${userId}/sessions${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch user sessions");
    return response.json();
  },

  async getSessionRecordings(
    sessionId: string,
    params: { user_id: string; dates: string[] },
  ): Promise<{
    session_id: string;
    user_id: string;
    dates: string[];
    recording_count: number;
    recordings: AudioRecording[];
    lesson_id?: string | null;
    lesson_name?: string | null;
    slide_index?: number | null;
  }> {
    const queryParams = new URLSearchParams({
      user_id: params.user_id,
      dates: params.dates.join(","),
    });
    const response = await fetch(
      `/api/proxy/audio-recordings/sessions/${sessionId}?${queryParams.toString()}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch session recordings");
    return response.json();
  },

  async getPlaybackUrl(key: string): Promise<PlaybackUrlResponse> {
    const params = new URLSearchParams();
    params.append("key", key);

    const response = await fetch(
      `/api/proxy/audio-recordings/play?${params.toString()}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch playback URL");
    return response.json();
  },

  // Load Test API
  async listLoadTests(limit: number = 20): Promise<ListTestsResponse> {
    const params = new URLSearchParams({ limit: limit.toString() });
    const response = await fetch(
      `/api/proxy/load-test/tests?${params}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to list load tests");
    return response.json();
  },

  async getLoadTestTargetPreview(
    classroomId: number,
  ): Promise<LoadTestTargetPreview> {
    const params = new URLSearchParams({ classroom_id: classroomId.toString() });
    const response = await fetch(
      `/api/internal/admin/load-test/target-preview?${params}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to load classroom target preview");
    return response.json();
  },

  async startLoadTest(config: LoadTestConfig): Promise<LoadTestStartResponse> {
    const params = new URLSearchParams({
      num_clients: config.num_clients.toString(),
      turns: config.turns.toString(),
      classroom_id: config.classroom_id.toString(),
      login_password: config.password,
      traffic_pattern: config.traffic_pattern,
      pattern_window_seconds: config.pattern_window_seconds.toString(),
      batch_size: config.batch_size.toString(),
      stop_mode: config.stop_mode,
      boot_buffer_seconds: config.boot_buffer_seconds.toString(),
    });
    // duration_seconds is only meaningful (and required) in duration mode.
    if (config.stop_mode === "duration") {
      params.set("duration_seconds", config.duration_seconds.toString());
    }
    const response = await fetch(`/api/proxy/load-test/start?${params}`, {
      ...fetchOptions,
      method: "POST",
    });
    if (!response.ok) throw await responseError(response, "Failed to start load test");
    return response.json();
  },

  async getLoadTestStatus(testId: string): Promise<LoadTestStatus> {
    const response = await fetch(
      `/api/proxy/load-test/${testId}/status`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch load test status");
    return response.json();
  },

  async getLoadTestClients(testId: string): Promise<GetClientsResponse> {
    const response = await fetch(
      `/api/proxy/load-test/${testId}/clients`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch load test clients");
    return response.json();
  },

  async getLoadTestAudio(
    testId: string,
    clientId: number,
  ): Promise<AudioUrlResponse> {
    const response = await fetch(
      `/api/proxy/load-test/${testId}/audio/${clientId}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch load test audio");
    return response.json();
  },

  async deleteLoadTest(testId: string): Promise<DeleteTestResponse> {
    const response = await fetch(`/api/proxy/load-test/${testId}`, {
      ...fetchOptions,
      method: "DELETE",
    });
    if (!response.ok) throw await responseError(response, "Failed to delete load test");
    return response.json();
  },

  async getLoadTestNotes(testId: string): Promise<LoadTestNotes> {
    const response = await fetch(
      `/api/proxy/load-test/${testId}/notes`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch load test notes");
    return response.json();
  },

  async saveLoadTestNotes(
    testId: string,
    notes: string,
  ): Promise<LoadTestNotes> {
    const response = await fetch(`/api/proxy/load-test/${testId}/notes`, {
      ...fetchOptions,
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ notes }),
    });
    if (!response.ok) throw await responseError(response, "Failed to save load test notes");
    return response.json();
  },

  // Service Latency Probe API
  async runLatencyProbe(
    request: LatencyProbeRequest,
  ): Promise<LatencyProbeResponse> {
    const response = await fetch(`/api/proxy/latency-probe/elasticache`, {
      ...fetchOptions,
      method: "POST",
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(
        await readApiError(response, "Failed to run latency probe"),
      );
    }
    return response.json();
  },

  // TTS Pool API
  async getTtsPoolAccounts(params?: {
    environment?: string;
  }): Promise<TtsPoolAccount[]> {
    const queryParams = new URLSearchParams();
    if (params?.environment) {
      queryParams.append("environment", params.environment);
    }

    const queryString = queryParams.toString();
    const response = await fetch(
      `/api/proxy/tts-pool/accounts${queryString ? `?${queryString}` : ""}`,
      fetchOptions,
    );

    if (!response.ok) {
      throw new Error(
        await readApiError(response, "Failed to fetch TTS pool accounts"),
      );
    }

    const payload = await response.json();
    return normalizeTtsPoolAccountsResponse(payload);
  },

  async updateTtsPoolAccount(
    accountId: string,
    body: TtsPoolAccountUpdateRequest,
  ): Promise<TtsPoolAccount | null> {
    const response = await fetch(
      `/api/proxy/tts-pool/accounts/${encodeURIComponent(accountId)}`,
      {
        ...fetchOptions,
        method: "PUT",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      throw new Error(
        await readApiError(response, "Failed to save TTS pool account"),
      );
    }

    const payload = await readJsonIfPresent<unknown>(response);
    return payload ? normalizeTtsPoolAccountResponse(payload) : null;
  },

  async resetTtsPoolCooldown(
    accountId: string,
    provider?: string,
  ): Promise<{ success: boolean; [key: string]: unknown }> {
    const queryParams = new URLSearchParams();
    if (provider) {
      queryParams.append("provider", provider);
    }
    const queryString = queryParams.toString();
    const response = await fetch(
      `/api/proxy/tts-pool/accounts/${encodeURIComponent(accountId)}/cooldown/reset${queryString ? `?${queryString}` : ""}`,
      {
        ...fetchOptions,
        method: "POST",
      },
    );

    if (!response.ok) {
      throw new Error(
        await readApiError(response, "Failed to reset TTS cooldown"),
      );
    }

    return (
      (await readJsonIfPresent<{ success: boolean; [key: string]: unknown }>(
        response,
      )) ?? { success: true }
    );
  },

  async getTtsPoolMetrics(params?: {
    environment?: string;
  }): Promise<TtsPoolMetrics | null> {
    const queryParams = new URLSearchParams();
    if (params?.environment) {
      queryParams.append("environment", params.environment);
    }

    const queryString = queryParams.toString();
    const response = await fetch(
      `/api/proxy/tts-pool/metrics${queryString ? `?${queryString}` : ""}`,
      fetchOptions,
    );

    if (!response.ok) {
      throw new Error(
        await readApiError(response, "Failed to fetch TTS pool metrics"),
      );
    }

    const payload = await readJsonIfPresent<unknown>(response);
    return payload ? normalizeTtsPoolMetricsResponse(payload) : null;
  },

  // Feature Flags API (local backend routes)
  async getFeatureFlagsUsers(
    params?: FeatureFlagsUsersParams,
  ): Promise<FeatureFlagsUsersResponse> {
    const queryParams = new URLSearchParams();
    if (params?.query) queryParams.append("query", params.query);
    if (params?.organization_id) queryParams.append("organization_id", params.organization_id);
    if (params?.role) queryParams.append("role", params.role);
    if (params?.classroom_id)
      queryParams.append("classroom_id", params.classroom_id.toString());
    if (params?.teacher_id)
      queryParams.append("teacher_id", params.teacher_id.toString());
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.sort_field)
      queryParams.append("sort_field", params.sort_field);
    if (params?.sort_order)
      queryParams.append("sort_order", params.sort_order);

    const queryString = queryParams.toString();
    const url = `/api/internal/admin/feature-flags/users${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch feature flags users");
    return response.json();
  },

  async getClassrooms(): Promise<Classroom[]> {
    const response = await fetch(
      `/api/internal/admin/classrooms`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch classrooms");
    return response.json();
  },

  async createClassroom(body: CreateClassroomRequest): Promise<Classroom> {
    const response = await fetch(`/api/internal/admin/classrooms`, {
      ...fetchOptions,
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!response.ok) throw await responseError(response, "Failed to create classroom");
    return response.json();
  },

  async updateClassroom(
    classroomId: number,
    body: UpdateClassroomRequest,
  ): Promise<Classroom> {
    const response = await fetch(`/api/internal/admin/classrooms/${classroomId}`, {
      ...fetchOptions,
      method: "PATCH",
      body: JSON.stringify(body),
    });
    if (!response.ok) throw await responseError(response, "Failed to update classroom");
    return response.json();
  },

  async getClassroomDeletionImpact(classroomId: number, signal?: AbortSignal): Promise<ClassroomDeletionImpact> {
    const response = await fetch(`/api/internal/admin/classrooms/${classroomId}/deletion-impact`, { ...fetchOptions, signal });
    if (!response.ok) throw new Error(await readApiError(response, "Failed to check classroom assignments and activity"));
    const impact = await response.json() as ClassroomDeletionImpact;
    if (!impact || impact.classroom_id !== classroomId || typeof impact.classroom_name !== "string" || !Number.isInteger(impact.student_count) || impact.student_count < 0 ||
        [impact.has_lesson_assignments, impact.has_student_sessions, impact.has_assessment_attempts, impact.sync_managed].some(value => typeof value !== "boolean")) {
      throw new Error("Incomplete classroom impact. Retry before deleting.");
    }
    return impact;
  },

  async deleteClassroom(classroomId: number): Promise<{ classroom_id: number; student_count: number }> {
    const response = await fetch(`/api/internal/admin/classrooms/${classroomId}`, { ...fetchOptions, method: "DELETE" });
    if (!response.ok) throw new Error(await readApiError(response, "Failed to delete classroom"));
    return response.json();
  },

  async createClassroomsFromCsv(body: { csv: string; teacher_id?: number }): Promise<{ created: number; skipped: number; classrooms: Classroom[] }> {
    const response = await fetch("/api/internal/admin/classrooms/create-import", { ...fetchOptions, method: "POST", body: JSON.stringify(body) });
    if (!response.ok) throw new Error(await readApiError(response, "Failed to import classrooms"));
    return response.json();
  },

  async getClassroomRelationships(
    classroomIds?: number[],
    options?: { include_students?: boolean },
  ): Promise<ClassroomRelationship[]> {
    const params = new URLSearchParams();
    if (classroomIds && classroomIds.length > 0) {
      params.append("classroom_ids", classroomIds.join(","));
    }
    if (options?.include_students !== undefined) {
      params.append("include_students", String(options.include_students));
    }
    const query = params.toString();
    const url = `/api/internal/admin/classrooms/relationships${query ? `?${query}` : ""}`;
    const response = await fetch(url, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch classroom relationships");
    return response.json();
  },

  async bulkAssignStudents(
    body: BulkAssignStudentsRequest,
  ): Promise<BulkAssignStudentsResponse> {
    const response = await fetch(
      `/api/internal/admin/classrooms/bulk-assign/students`,
      {
        ...fetchOptions,
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) throw await responseError(response, "Failed to assign students");
    return response.json();
  },

  async bulkAssignTeachers(
    body: BulkAssignTeachersRequest,
  ): Promise<BulkAssignTeachersResponse> {
    const response = await fetch(
      `/api/internal/admin/classrooms/bulk-assign/teachers`,
      {
        ...fetchOptions,
        method: "POST",
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) throw await responseError(response, "Failed to assign teachers");
    return response.json();
  },

  async exportClassroomRelationships(
    format: "json" | "csv" = "json",
    classroomIds?: number[],
  ): Promise<ExportResponse | string> {
    const params = new URLSearchParams();
    params.append("format", format);
    if (classroomIds && classroomIds.length > 0) {
      params.append("classroom_ids", classroomIds.join(","));
    }
    const response = await fetch(
      `/api/internal/admin/classrooms/export?${params.toString()}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to export classroom relationships");
    return format === "csv" ? response.text() : response.json();
  },

  async importClassroomRelationships(
    body: ImportRequest,
  ): Promise<ClassroomImportResponse> {
    const response = await fetch(`/api/internal/admin/classrooms/import`, {
      ...fetchOptions,
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!response.ok) throw await responseError(response, "Failed to import classroom relationships");
    return response.json();
  },

  async getTeachersForFilter(options?: {
    forceRefresh?: boolean;
    ttlMs?: number;
  }): Promise<Teacher[]> {
    const ttlMs = options?.ttlMs ?? TEACHERS_CACHE_TTL_MS;
    const now = Date.now();
    if (
      !options?.forceRefresh &&
      teachersForFilterCache &&
      teachersForFilterCache.expiresAt > now
    ) {
      return teachersForFilterCache.data;
    }

    const response = await fetch(`/api/internal/admin/teachers`, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch teachers");
    const data = (await response.json()) as Teacher[];
    teachersForFilterCache = {
      data,
      expiresAt: now + ttlMs,
    };
    return data;
  },

  async getTeachersForFilterPage(params?: {
    query?: string;
    limit?: number;
    offset?: number;
  }): Promise<TeachersFilterPage> {
    const queryParams = new URLSearchParams();
    if (params?.query !== undefined) {
      queryParams.append("query", params.query);
    }
    queryParams.append("limit", String(params?.limit ?? 50));
    queryParams.append("offset", String(params?.offset ?? 0));
    const response = await fetch(
      `/api/internal/admin/teachers?${queryParams.toString()}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch teachers");
    return response.json();
  },

  // Prompt Service API
  async getPrompts(applicationId?: string, org?: string, orgScope?: string, showHidden?: boolean): Promise<Prompt[]> {
    const params = new URLSearchParams();
    if (applicationId) params.append("application_id", applicationId);
    if (orgScope) {
      params.append("org_scope", orgScope);
    } else if (org) {
      params.append("org", org);
    }
    if (showHidden) params.append("show_hidden", "true");
    const query = params.toString();
    const url = `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.PROMPTS}${query ? `?${query}` : ""}`;
    const response = await fetch(url, fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch prompts");
    const data = (await response.json()) as Prompt[];
    return data.map((prompt) => ({
      ...prompt,
      tags: Array.isArray(prompt.tags) ? prompt.tags : [],
    }));
  },

  async getPrompt(applicationId: string, promptId: string): Promise<Prompt> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.PROMPT(applicationId, promptId)}`,
      fetchOptions
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch prompt");
    const prompt = (await response.json()) as Prompt;
    return {
      ...prompt,
      tags: Array.isArray(prompt.tags) ? prompt.tags : [],
    };
  },

  async createPrompt(request: PromptCreateRequest): Promise<Prompt> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.PROMPTS}`,
      {
        method: "POST",
        ...fetchOptions,
        body: JSON.stringify(request),
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to create prompt");
    return response.json();
  },

  async updatePrompt(
    applicationId: string,
    promptId: string,
    updates: Partial<Prompt>
  ): Promise<Prompt> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.PROMPT(applicationId, promptId)}`,
      {
        method: "PATCH",
        ...fetchOptions,
        body: JSON.stringify(updates),
      }
    );
    if (!response.ok) {
      throw new Error(await readApiError(response, "Failed to update prompt"));
    }
    return response.json();
  },

  async getPromptVersions(applicationId: string, promptId: string): Promise<PromptVersion[]> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.PROMPT_VERSIONS(applicationId, promptId)}`,
      fetchOptions
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch versions");
    return response.json();
  },

  async getPromptVersionStatuses(
    applicationId: string,
    promptId: string
  ): Promise<PromptVersionStatus[]> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.PROMPT_VERSION_STATUSES(applicationId, promptId)}`,
      fetchOptions
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch versions");
    return response.json();
  },

  async getPromptVersion(
    applicationId: string,
    promptId: string,
    version: string
  ): Promise<PromptVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.PROMPT_VERSION(applicationId, promptId, version)}`,
      fetchOptions
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch version");
    return response.json();
  },

  async createPromptVersion(
    applicationId: string,
    promptId: string,
    request: VersionCreateRequest
  ): Promise<PromptVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.PROMPT_VERSIONS(applicationId, promptId)}`,
      {
        method: "POST",
        ...fetchOptions,
        body: JSON.stringify(request),
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to create version");
    return response.json();
  },

  async activatePromptVersion(
    applicationId: string,
    promptId: string,
    version: string
  ): Promise<void> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.PROMPT_VERSION(applicationId, promptId, version)}/activate`,
      {
        method: "POST",
        ...fetchOptions,
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to activate version");
  },

  // Experiment API
  async getExperiments(): Promise<Experiment[]> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.EXPERIMENTS}`,
      fetchOptions
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch experiments");
    return response.json();
  },

  async getExperiment(experimentId: string): Promise<Experiment> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.EXPERIMENT(experimentId)}`,
      fetchOptions
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch experiment");
    return response.json();
  },

  async createExperiment(request: ExperimentCreateRequest): Promise<Experiment> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.EXPERIMENTS}`,
      {
        method: "POST",
        ...fetchOptions,
        body: JSON.stringify(request),
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to create experiment");
    return response.json();
  },

  async startExperiment(experimentId: string): Promise<Experiment> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.EXPERIMENT_START(experimentId)}`,
      {
        method: "POST",
        ...fetchOptions,
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to start experiment");
    return response.json();
  },

  async stopExperiment(experimentId: string): Promise<Experiment> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.EXPERIMENT_STOP(experimentId)}`,
      {
        method: "POST",
        ...fetchOptions,
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to stop experiment");
    return response.json();
  },

  // Tutor Version API
  async getTutorVersions(applicationId: string): Promise<TutorVersion[]> {
    const response = await fetch(
      `/api/proxy/prompt-config/tutor-versions/${applicationId}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch tutor versions");
    return response.json();
  },

  async getTutorVersion(applicationId: string, tutorVersionId: string): Promise<TutorVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION(applicationId, tutorVersionId)}`,
      fetchOptions
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch tutor version");
    return response.json();
  },

  async createTutorVersion(request: TutorVersionCreateRequest): Promise<TutorVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}/tutor-versions`,
      {
        method: "POST",
        ...fetchOptions,
        body: JSON.stringify(request),
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to create tutor version");
    return response.json();
  },

  async updateTutorVersion(
    applicationId: string,
    tutorVersionId: string,
    updates: {
      name?: string;
      description?: string;
      status?: string;
      prompts?: PromptSnapshot[];
      llm_config?: LLMConfig;
      llm_configs?: NamedLLMConfig[];
      stt_config?: STTConfig | null;
      tts_config?: TTSConfig | null;
    }
  ): Promise<TutorVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION(applicationId, tutorVersionId)}`,
      {
        method: "PATCH",
        ...fetchOptions,
        body: JSON.stringify(updates),
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to update tutor version");
    return response.json();
  },

  async publishTutorVersion(applicationId: string, tutorVersionId: string): Promise<TutorVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION_PUBLISH(applicationId, tutorVersionId)}`,
      {
        method: "POST",
        ...fetchOptions,
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to publish tutor version");
    return response.json();
  },

  async archiveTutorVersion(applicationId: string, tutorVersionId: string): Promise<TutorVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION_ARCHIVE(applicationId, tutorVersionId)}`,
      {
        method: "POST",
        ...fetchOptions,
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to archive tutor version");
    return response.json();
  },

  async setDefaultTutorVersion(applicationId: string, tutorVersionId: string): Promise<TutorVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION_SET_DEFAULT(applicationId, tutorVersionId)}`,
      {
        method: "POST",
        ...fetchOptions,
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to set default tutor version");
    return response.json();
  },

  async getOrgDefaultTutorVersionMap(applicationId: string): Promise<Record<string, string>> {
    try {
      const response = await fetch(
        `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION_ORG_DEFAULTS(applicationId)}`,
        fetchOptions
      );
      if (!response.ok) return {};
      return response.json();
    } catch {
      return {};
    }
  },

  async removeOrgDefaultTutorVersion(applicationId: string, org: string): Promise<void> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION_REMOVE_ORG_DEFAULT(applicationId, org)}`,
      { method: "DELETE", ...fetchOptions }
    );
    if (!response.ok) throw await responseError(response, "Failed to remove org default tutor version");
  },

  async setOrgDefaultTutorVersion(applicationId: string, org: string, tutorVersionId: string): Promise<void> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION_SET_ORG_DEFAULT(applicationId)}`,
      {
        method: "POST",
        ...fetchOptions,
        body: JSON.stringify({ org, tutor_version_id: tutorVersionId }),
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to set org default tutor version");
  },

  async getOrganizations(): Promise<{ id: string; name: string }[]> {
    const response = await fetch("/api/internal/admin/organizations", {
      credentials: "include",
    });
    if (!response.ok) throw await responseError(response, "Failed to fetch organizations");
    return response.json();
  },

  async deleteTutorVersion(applicationId: string, tutorVersionId: string): Promise<void> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION(applicationId, tutorVersionId)}`,
      {
        method: "DELETE",
        ...fetchOptions,
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to delete tutor version");
  },

  async exportTutorVersion(applicationId: string, tutorVersionId: string): Promise<unknown> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION_EXPORT(applicationId, tutorVersionId)}`,
      fetchOptions
    );
    if (!response.ok) throw await responseError(response, "Failed to export tutor version");
    return response.json();
  },

  // Dev TV API
  async createDevTutorVersion(data: {
    application_id: string;
    name: string;
    description?: string;
    org?: string;
    prompts: PromptSnapshot[];
    llm_config: LLMConfig;
    llm_configs?: NamedLLMConfig[];
    stt_config?: STTConfig;
    tts_config?: TTSConfig;
    created_by?: string;
    applications?: ApplicationTVSection[];
  }): Promise<TutorVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}/tutor-versions/dev`,
      {
        method: "POST",
        ...fetchOptions,
        body: JSON.stringify(data),
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to create dev tutor version");
    return response.json();
  },

  async promoteDevTutorVersion(appId: string, tvId: string): Promise<TutorVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}/tutor-versions/${appId}/${tvId}/promote`,
      {
        method: "POST",
        ...fetchOptions,
      }
    );
    if (!response.ok) throw await responseError(response, "Failed to promote dev tutor version");
    return response.json();
  },

  async resolveTutorVersion(appId: string, tvId: string): Promise<ResolvedTutorVersion> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}/runtime/tutor-version/${appId}/${tvId}`,
      fetchOptions
    );
    if (!response.ok) throw await responseError(response, "Failed to resolve tutor version");
    return response.json();
  },

  /**
   * The gateway version with this id, exported, or null when prompt-config
   * has none. Every other failure throws.
   */
  async exportGatewayVersion(tutorVersionId: string): Promise<Record<string, unknown> | null> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION_EXPORT("gateway", tutorVersionId)}`,
      fetchOptions
    );
    if (response.status === 404) return null;
    if (!response.ok) {
      const reason = await readApiError(response, "Failed to export tutor version");
      throw new Error(`Cannot export the gateway version ${tutorVersionId}: ${reason}`);
    }
    return response.json();
  },

  /**
   * The import file for a published tutor version: the body of prompt-config's
   * batch import. For ai-tutor it also carries the gateway version, which has
   * the same id, so one import moves both. When there is no gateway version
   * the file holds ai-tutor only; any other gateway failure fails the export,
   * so a file never leaves out a gateway version that exists.
   */
  async exportTutorVersionBundle(
    primaryApplicationId: string,
    primaryTutorVersionId: string,
  ): Promise<TutorVersionBundle> {
    const [primaryExport, gatewayExport] = await Promise.all([
      this.exportTutorVersion(primaryApplicationId, primaryTutorVersionId),
      primaryApplicationId === "ai-tutor"
        ? this.exportGatewayVersion(primaryTutorVersionId)
        : Promise.resolve(null),
    ]);

    const primaryPayload = primaryExport as Record<string, unknown>;
    const { name, description, created_by } = primaryPayload;

    const toImportApplication = (
      applicationId: string,
      payload: Record<string, unknown>,
    ) => ({
      application_id: applicationId,
      prompts: payload.prompts ?? [],
      llm_config: payload.llm_config ?? {},
      ...(payload.llm_configs ? { llm_configs: payload.llm_configs } : {}),
      ...(payload.stt_config ? { stt_config: payload.stt_config } : {}),
      ...(payload.tts_config ? { tts_config: payload.tts_config } : {}),
    });

    const applications: Record<string, unknown>[] = [
      toImportApplication(primaryApplicationId, primaryPayload),
    ];

    if (gatewayExport) {
      applications.push(toImportApplication("gateway", gatewayExport));
    }

    return {
      name: String(name ?? ""),
      // prompt-config's batch import requires a description string.
      description: typeof description === "string" ? description : "",
      created_by: typeof created_by === "string" ? created_by : undefined,
      applications,
    };
  },

  /**
   * Imports a tutor-version export file into this environment. A version moves
   * from dev to prod this way: export it in the dev dashboard, then import the
   * file in the prod dashboard.
   */
  async importTutorVersionBundle(bundle: unknown): Promise<unknown> {
    const response = await fetch(
      `${PROMPT_SERVICE_CONFIG.BASE_URL}${PROMPT_SERVICE_CONFIG.ENDPOINTS.TUTOR_VERSION_IMPORT_BATCH}`,
      {
        method: "POST",
        ...fetchOptions,
        body: JSON.stringify(bundle),
      }
    );
    if (!response.ok) {
      throw new Error(await readApiError(response, "Failed to import the tutor version"));
    }
    return response.json();
  },

  async getAnalyticsSchools(): Promise<AnalyticsSchoolsData> {
    const response = await fetch("/api/proxy/analytics/schools", fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch analytics schools");
    return response.json();
  },

  async getAnalyticsFilters(): Promise<AnalyticsFiltersData> {
    const response = await fetch("/api/proxy/analytics/filters", fetchOptions);
    if (!response.ok) throw await responseError(response, "Failed to fetch analytics filters");
    return response.json();
  },

  async getAnalyticsHeatmap(
    params?: AnalyticsFilterParams,
  ): Promise<AnalyticsHeatmapResponse> {
    const response = await fetch(
      `/api/proxy/analytics/heatmap${buildAnalyticsQuery(params)}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch heatmap data");
    return response.json();
  },

  async getAnalyticsOverview(
    params?: AnalyticsFilterParams,
  ): Promise<AnalyticsOverview> {
    const response = await fetch(
      `/api/proxy/analytics/overview${buildAnalyticsQuery(params)}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch analytics overview");
    return response.json();
  },

  async getAnalyticsStudents(
    params?: AnalyticsFilterParams,
  ): Promise<AnalyticsStudentsResponse> {
    const response = await fetch(
      `/api/proxy/analytics/students${buildAnalyticsQuery(params)}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch analytics students");
    return response.json();
  },

  async getAnalyticsStudentDetail(
    userId: number,
    params?: AnalyticsFilterParams,
  ): Promise<AnalyticsStudentDetail> {
    const response = await fetch(
      `/api/proxy/analytics/students/${userId}${buildAnalyticsQuery(params)}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch student detail");
    return response.json();
  },

  async getAnalyticsSessionDetail(
    userId: number,
    sessionId: string,
  ): Promise<AnalyticsSessionDetail> {
    const response = await fetch(
      `/api/proxy/analytics/students/${userId}/sessions/${sessionId}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch session detail");
    return response.json();
  },

  async getAnalyticsWaste(
    params?: AnalyticsFilterParams,
  ): Promise<AnalyticsWasteResponse> {
    const response = await fetch(
      `/api/proxy/analytics/waste${buildAnalyticsQuery(params)}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch waste analytics");
    return response.json();
  },

  async getSessionsMetrics(
    dimension: SessionsDimension,
    params?: AnalyticsFilterParams,
  ): Promise<SessionsMetricsResponse> {
    const response = await fetch(
      `/api/proxy/analytics/sessions-metrics${buildAnalyticsQuery({ ...params, dimension })}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch sessions metrics");
    return response.json();
  },

  async getStudentsMetrics(
    params?: AnalyticsFilterParams,
  ): Promise<StudentsMetricsResponse> {
    const response = await fetch(
      `/api/proxy/analytics/kids-metrics${buildAnalyticsQuery(params)}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch students metrics");
    return response.json();
  },

  async getFirstPass(params?: AnalyticsFilterParams): Promise<FirstPassResponse> {
    const response = await fetch(
      `/api/proxy/analytics/first-pass${buildAnalyticsQuery(params)}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch first pass data");
    return response.json();
  },

  async getSessionTimeline(sessionId: string): Promise<SessionTimelineResponse> {
    const response = await fetch(
      `/api/proxy/analytics/session-timeline/${sessionId}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch session timeline");
    return response.json();
  },

  async getSessionMessages(sessionId: string): Promise<SessionMessagesResponse> {
    const response = await fetch(
      `/api/proxy/analytics/session-messages/${sessionId}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch session messages");
    return response.json();
  },

  async getSlideDifficulty(
    params?: AnalyticsFilterParams,
  ): Promise<SlideDifficultyResponse> {
    const response = await fetch(
      `/api/proxy/analytics/slide-difficulty${buildAnalyticsQuery(params)}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to fetch slide difficulty");
    return response.json();
  },

  async getAnalyticsExport(
    params?: AnalyticsFilterParams,
  ): Promise<AnalyticsExportResponse> {
    const response = await fetch(
      `/api/proxy/analytics/export${buildAnalyticsQuery(params)}`,
      fetchOptions,
    );
    if (!response.ok) throw await responseError(response, "Failed to load export data");
    return response.json();
  },

};

function buildAnalyticsQuery(params?: AnalyticsFilterParams): string {
  const queryParams = new URLSearchParams();
  if (params?.dimension) queryParams.append("dimension", params.dimension);
  if (params?.start_date) queryParams.append("start_date", params.start_date);
  if (params?.end_date) queryParams.append("end_date", params.end_date);
  if (params?.classroom_id != null) {
    queryParams.append("classroom_id", String(params.classroom_id));
  }
  if (params?.lesson_id != null) {
    queryParams.append("lesson_id", String(params.lesson_id));
  }
  if (params?.cohort_id) queryParams.append("cohort_id", params.cohort_id);
  if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
  if (params?.sort_dir) queryParams.append("sort_dir", params.sort_dir);
  if (params?.user_ids?.length) {
    queryParams.append("user_ids", params.user_ids.join(","));
  }

  const query = queryParams.toString();
  return query ? `?${query}` : "";
}

export interface AudioUser {
  user_id: string;
  recording_count: number;
  session_count: number;
  last_recording: string;
}

export interface AudioSession {
  session_id: string;
  dates: string[];
  recording_count: number;
  first_recording: string;
  last_recording: string;
  lesson_id?: string | null;
  lesson_name?: string | null;
  slide_index?: number | null;
}

export interface AudioRecording {
  key: string;
  turn_id: string;
  timestamp: string;
  size_bytes: number;
  presigned_url?: string;
  // From S3 object metadata: which lesson/bite the turn was recorded on.
  lesson_id?: string | null;
  slide_index?: number | null;
  // "NOTEPAD" for a notepad clip; null for the tutor's own recordings.
  surface?: string | null;
  // True when the stream ended before the student finished the turn.
  partial?: boolean;
}

export interface PlaybackUrlResponse {
  key: string;
  url: string;
  expires_in: number;
}

// Load Test Types
export type TrafficPattern =
  | "all_at_once"
  | "linear_ramp"
  | "random_within_window"
  | "burst_groups";

export type StopMode = "turns" | "duration";

export interface LoadTestConfig {
  num_clients: number;
  turns: number;
  // The classroom drives everything: its roster supplies the per-client student
  // logins and its assigned lesson is the one each client opens. The backend
  // derives the lesson from the classroom, so no lesson id/name is sent.
  classroom_id: number;
  // Shared password for every student in the classroom roster. Usernames are
  // derived per-client from the roster on the backend, not sent from here.
  password: string;
  // Arrival pattern controlling when each client starts. The backend turns this
  // into a per-client start offset; clients sleep to their slot before login.
  traffic_pattern: TrafficPattern;
  // Window (seconds) the arrivals are spread over (ramp/random), or the gap
  // between bursts (burst_groups). Ignored for all_at_once.
  pattern_window_seconds: number;
  // Clients per burst for the burst_groups pattern. Ignored otherwise.
  batch_size: number;
  // Stop mode: 'turns' runs a fixed turn count, 'duration' runs to a deadline.
  stop_mode: StopMode;
  // Per-client run length (seconds) when stop_mode is 'duration'.
  duration_seconds: number;
  // Lead time (seconds) before the arrival schedule begins, so Fargate tasks
  // have time to boot and the pattern isn't collapsed by boot jitter.
  boot_buffer_seconds: number;
}

export interface LoadTestTargetPreview {
  classroom: { id: number; name: string | null };
  teacher: {
    user_id: number;
    name: string | null;
    username: string | null;
  } | null;
  organization: { id: string; name: string } | null;
  org_admin: {
    user_id: number;
    name: string | null;
    username: string | null;
  } | null;
  lessons: { id: number; name: string | null }[];
  roster: {
    student_count: number;
    sample_usernames: string[];
  };
}

export interface LoadTestStartResponse {
  test_id: string;
  status: "started";
  tasks_started: number;
  tasks_requested: number;
  task_arns: string[];
  errors: string[];
  config: {
    num_clients: number;
    lesson_id: number;
    classroom_id: number;
    lesson_name: string;
    turns: number;
    target_url: string;
  };
}

export interface LoadTestStatus {
  test_id: string;
  running_tasks: number;
  stopped_tasks: number;
  completed_clients: number;
  status: "running" | "completed";
}

export interface ClientMetrics {
  testId: string;
  clientId: number;
  accountId: string;
  lessonId: number;
  timeToLoginMs: number;
  wsConnectTimeMs: number;
  wsReadyWaitMs?: number | null;
  wsCreateToOpenMs?: number | null;
  startClickToWsCreateMs?: number | null;
  timeToFirstAudioMs: number;
  totalDurationMs: number;
  turnsCompleted: number;
  turnsAttempted: number;
  audioChunksReceived: number;
  audioFileKey: string;
  // Raw per-turn latency samples uploaded by the client (the backend pools
  // these across clients for the summary percentiles). One entry per turn that
  // produced data; audio gaps are a list-of-gaps per turn.
  userPerceivedTtfaMsPerTurn?: number[];
  audioGapsMsPerTurn?: number[][];
  errors: string[];
  success: boolean;
}

export interface PercentileStats {
  p50: number | null;
  p90: number | null;
  p95: number | null;
  count?: number;
}

export interface TestSummary {
  test_id: string;
  total_clients: number;
  successful_clients: number;
  failed_clients: number;
  // Login timing pooled across clients (one sample per client) as p50/p90/p95.
  time_to_login_ms?: PercentileStats;
  avg_ws_connect_time_ms: number;
  avg_ws_ready_wait_ms?: number | null;
  avg_ws_create_to_open_ms?: number | null;
  avg_start_click_to_ws_create_ms?: number | null;
  avg_time_to_first_audio_ms: number;
  avg_total_duration_ms: number;
  total_turns_completed: number;
  total_audio_chunks: number;
  // Percentiles pooled across all turns x clients (added by the load-test
  // backend's build_test_summary).
  user_perceived_ttfa_ms?: PercentileStats;
  audio_gap_ms?: PercentileStats;
}

export interface ClientWithAudio {
  client_id: number;
  metrics: ClientMetrics;
  audio_url: string | null;
}

export interface TestListItem {
  test_id: string;
  client_count: number;
  audio_files: number;
  last_modified: string | null;
  // Test-level metadata from the backend's meta.json. Null for tests started
  // before meta.json existed — the UI leaves those fields blank.
  num_clients: number | null;
  turns: number | null;
  started_at: string | null;
  lesson_name: string | null;
  classroom_id: number | null;
}

export interface ListTestsResponse {
  tests: TestListItem[];
  total: number;
}

export interface GetClientsResponse {
  summary: TestSummary;
  clients: ClientWithAudio[];
  audio_expires_in: number;
}

export interface AudioUrlResponse {
  audio_url: string;
  expires_in: number;
}

export interface DeleteTestResponse {
  test_id: string;
  deleted_files: number;
  status: "deleted";
}

export interface LoadTestNotes {
  test_id: string;
  notes: string;
  updated_at: string | null;
}

// Service Latency Probe Types
export type LatencyProbeTarget = "redis";

export interface LatencyProbeRequest {
  operation: string;
  iterations: number;
  concurrency: number;
  payload_size_bytes: number;
}

export interface LatencyProbeStats {
  count: number;
  errors: number;
  min_ms: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  max_ms: number;
  mean_ms: number;
}

export interface LatencyProbeResponse {
  stage: string;
  target: LatencyProbeTarget;
  operation: string;
  iterations: number;
  concurrency: number;
  payload_size_bytes: number;
  latency: LatencyProbeStats;
}

// TTS Pool Types
export interface TtsPoolAccount {
  provider?: string | null;
  account_id: string;
  environment?: string | null;
  enabled?: boolean;
  pronunciation_dict_id?: string | null;
  healthy?: boolean | null;
  is_healthy?: boolean | null;
  is_cooling_down?: boolean | null;
  cooldown_until?: string | null;
  cooldown_expires_at?: string | null;
  cooldown_reason?: string | null;
  api_key_masked?: string | null;
  masked_api_key?: string | null;
  voice?: string | null;
  default_voice?: string | null;
  voice_overrides?: Record<string, unknown> | null;
  weight?: number | null;
  priority?: number | null;
  selection_count?: number | null;
  success_count?: number | null;
  before_audio_failure_count?: number | null;
  active_utterances?: number | null;
  failover_count?: number | null;
  fallback_to_elevenlabs_count?: number | null;
  consecutive_failures?: number | null;
  requests_1h?: number | null;
  request_count_1h?: number | null;
  requests_24h?: number | null;
  request_count_24h?: number | null;
  failures_1h?: number | null;
  failure_count_1h?: number | null;
  failures_24h?: number | null;
  failure_count_24h?: number | null;
  failovers_1h?: number | null;
  failover_count_1h?: number | null;
  failovers_24h?: number | null;
  failover_count_24h?: number | null;
  last_used_at?: string | null;
  last_error?: string | null;
  [key: string]: unknown;
}

export interface TtsPoolEnvironmentMetrics {
  environment: string;
  provider?: string | null;
  total_accounts?: number;
  enabled_accounts?: number;
  healthy_accounts?: number;
  cooling_down_accounts?: number;
  active_utterances?: number;
  selection_count?: number;
  success_count?: number;
  before_audio_failure_count?: number;
  failover_count?: number;
  fallback_to_elevenlabs_count?: number;
  requests_1h?: number;
  request_count_1h?: number;
  requests_24h?: number;
  request_count_24h?: number;
  failures_1h?: number;
  failure_count_1h?: number;
  failures_24h?: number;
  failure_count_24h?: number;
  failovers_1h?: number;
  failover_count_1h?: number;
  failovers_24h?: number;
  failover_count_24h?: number;
  [key: string]: unknown;
}

export interface TtsPoolProviderMetrics extends TtsPoolEnvironmentMetrics {
  provider: string;
}

export interface TtsPoolMetrics {
  environment?: string | null;
  total_accounts?: number;
  enabled_accounts?: number;
  healthy_accounts?: number;
  cooling_down_accounts?: number;
  active_utterances?: number;
  selection_count?: number;
  success_count?: number;
  before_audio_failure_count?: number;
  failover_count?: number;
  fallback_to_elevenlabs_count?: number;
  requests_1h?: number;
  request_count_1h?: number;
  requests_24h?: number;
  request_count_24h?: number;
  failures_1h?: number;
  failure_count_1h?: number;
  failures_24h?: number;
  failure_count_24h?: number;
  failovers_1h?: number;
  failover_count_1h?: number;
  failovers_24h?: number;
  failover_count_24h?: number;
  environments?: TtsPoolEnvironmentMetrics[];
  by_environment?: TtsPoolEnvironmentMetrics[];
  providers?: TtsPoolProviderMetrics[];
  by_provider?: TtsPoolProviderMetrics[];
  [key: string]: unknown;
}

export interface TtsPoolAccountUpdateRequest {
  provider?: string;
  api_key?: string;
  enabled?: boolean;
  voice?: string;
  pronunciation_dict_id?: string;
  voice_overrides?: Record<string, unknown>;
  weight?: number;
  priority?: number;
  environment?: string;
}

// Feature Flags Types
export interface UserWithFlags {
  id: number;
  name: string;
  username: string | null;
  email: string;
  role: "STUDENT" | "TEACHER" | "ORG_ADMIN";
  classroom_id?: number;
  classroom_name?: string;
  config: UserConfig;
  overrides: UserConfig;
  updated_at: string;
}

export interface UserRoleCounts {
  admins: number;
  teachers: number;
  students: number;
}

export interface FeatureFlagsUsersResponse {
  environment: string;
  users: UserWithFlags[];
  total: number;
  /** Role breakdown of the whole filtered set, not just this page. */
  counts: UserRoleCounts;
  page: number;
  limit: number;
}

export interface FeatureFlagsUsersParams {
  query?: string;
  role?: "STUDENT" | "TEACHER" | "ORG_ADMIN";
  classroom_id?: number;
  organization_id?: string;
  teacher_id?: number;
  page?: number;
  limit?: number;
  /** Sorting is applied across all matches server-side, before pagination. */
  sort_field?: "name" | "username" | "email";
  sort_order?: "asc" | "desc";
}

export interface Classroom {
  id: number;
  name: string;
  description?: string | null;
  teacher_id?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface ClassroomRelationship {
  classroom_id: number;
  classroom_name: string;
  description: string | null;
  teacher_id: number | null;
  organization_id?: string | null;
  teacher_name: string | null;
  teacher_username?: string | null;
  teacher_email: string | null;
  student_count: number;
  student_ids: number[];
  students: Student[];
  sync_managed?: boolean;
  imported_student_count?: number | null;
}

export interface CreateClassroomRequest {
  name: string;
  description?: string;
  teacher_id?: number | null;
}

export interface UpdateClassroomRequest {
  name?: string;
  description?: string | null;
  teacher_id?: number | null;
}

export interface BulkAssignStudentsRequest {
  classroom_id: number;
  student_ids: number[];
  mode: "add" | "replace" | "remove";
}

export interface BulkAssignError {
  classroom_id: number;
  student_id?: number;
  teacher_id?: number | null;
  reason: string;
}

export interface BulkAssignStudentsResponse {
  classroom_id: number;
  requested: number;
  added: number;
  removed: number;
  skipped: number;
  errors: BulkAssignError[];
}

export interface BulkAssignTeachersRequest {
  assignments: Array<{
    classroom_id: number;
    teacher_id: number | null;
  }>;
}

export interface BulkAssignTeachersResponse {
  requested: number;
  updated: number;
  skipped: number;
  errors: BulkAssignError[];
}

export interface ImportRow {
  classroom_id?: number;
  classroom_name?: string;
  description?: string | null;
  teacher_id?: number | null;
  student_ids?: number[];
}

export interface ImportRequest {
  rows: ImportRow[];
  dry_run: boolean;
  confirm_import?: boolean;
  student_mode?: "add" | "replace";
}

export interface ClassroomImportRowResult {
  row: number;
  classroom_id?: number;
  classroom_name?: string;
  action: "create" | "update" | "skip";
  added: number;
  removed: number;
  skipped: number;
  errors: string[];
}

export interface ClassroomImportError {
  row: number;
  reason: string;
}

export interface ClassroomImportResponse {
  requested: number;
  created_classrooms: number;
  updated_classrooms: number;
  created_relationships: number;
  removed_relationships: number;
  skipped: number;
  errors: ClassroomImportError[];
  rows: ClassroomImportRowResult[];
  dry_run: boolean;
}

export interface ExportResponse {
  generated_at: string;
  classrooms: ClassroomRelationship[];
}

// Lesson Config Types
export interface SlideMetadata extends Record<string, unknown> {
  scratchpad?: { enabled: boolean };
  calculator?: { enabled: boolean };
}

export interface SlideInfo {
  id: number;
  name: string | null;
  slide_index: number | null;
  slide_status: string;
  slide_type: string | null;
  metadata: SlideMetadata | null;
}

export interface SlideMetadataUpdateResponse {
  success: boolean;
  id: number;
  metadata: SlideMetadata;
}

export interface SlideMetadataBulkUpdateItem {
  id: number;
  metadata: SlideMetadata;
}

export interface SlideMetadataBulkUpdateResponse {
  success: boolean;
  updated_count: number;
  slides: SlideMetadataBulkUpdateItem[];
}

export interface LessonDetail {
  lesson_id: number;
  lesson_name: string;
  teacher_name: string | null;
  is_published: boolean;
  curriculum_metadata: Record<string, unknown> | null;
  slides: SlideInfo[];
}

export interface ExportSlide {
  id: number;
  name: string | null;
  slide_index: number | null;
  slide_status: string;
  slide_type: string | null;
  metadata: Record<string, unknown> | null;
  content: unknown | null;
  flow: unknown | null;
}

export interface LessonExport {
  lesson: {
    id: number;
    name: string;
    description: string | null;
    teacher_name: string | null;
    teacher_id: number;
    is_published: boolean;
    created_at: string | null;
    updated_at: string | null;
    curriculum_metadata: Record<string, unknown> | null;
  };
  slides: ExportSlide[];
}

export interface AnalyticsFilterOption {
  id: number;
  name: string;
}

export interface AnalyticsFiltersData {
  classrooms: AnalyticsFilterOption[];
  lessons: AnalyticsFilterOption[];
}

export interface AnalyticsSchoolClassroom {
  id: string;
  name: string;
  user_ids: number[];
  count: number;
}

export interface AnalyticsSchool {
  id: string;
  name: string;
  classrooms: AnalyticsSchoolClassroom[];
}

export interface AnalyticsSchoolsData {
  schools: AnalyticsSchool[];
}

export interface AnalyticsOverview {
  total_sessions: number;
  total_students: number;
  unique_lessons: number;
  completed_sessions: number;
  avg_completion_pct: number;
  avg_time_spent_ms: number;
  avg_tutor_msgs_per_session: number;
  avg_student_msgs_per_session: number;
  weekly_trend: { week: string; sessions: number; avg_completion: number }[];
  completion_distribution: { bucket: string; count: number }[];
  status_distribution: { status: string; count: number }[];
  daily_trend: { date: string; sessions: number; completed: number; avg_completion: number }[];
  interactions_trend: { date: string; avg_tutor_msgs: number; avg_student_msgs: number; avg_qa_responses: number }[];
}

export interface AnalyticsStudentRow {
  user_id: number;
  name: string | null;
  total_sessions: number;
  avg_completion_pct: number;
  avg_time_spent_ms: number;
  total_bites_correct: number;
  total_bites_incorrect: number;
  lesson_statuses: Record<string, number>;
  avg_exit_ticket_score: number | null;
}

export interface AnalyticsStudentsResponse {
  students: AnalyticsStudentRow[];
  total: number;
}

export interface AnalyticsStudentSession {
  session_id: string;
  lesson_id: number;
  lesson_name: string | null;
  classroom_id: number | null;
  session_start_ts: number | null;
  session_end_ts: number | null;
  time_spent_ms: number;
  completion_pct: number;
  lesson_status: string;
  max_slide_reached: number | null;
  total_slides: number | null;
}

export interface AnalyticsStudentDetail {
  user_id: number;
  name: string | null;
  sessions: AnalyticsStudentSession[];
  total_sessions: number;
}

export interface AnalyticsBiteDetail {
  slide_idx: number;
  bite_status: string;
  tutor_support_level: string | null;
  time_spent_ms: number;
  attempts_total: number;
  attempts_correct: number;
  attempts_incorrect: number;
  student_asked_count: number;
  ai_asked_count: number;
}

export interface AnalyticsSessionDetail {
  session_id: string;
  user_id: number;
  lesson_id: number;
  lesson_name: string | null;
  time_spent_ms: number;
  completion_pct: number;
  lesson_status: string;
  bites: AnalyticsBiteDetail[];
}

export interface AnalyticsWasteRow {
  session_id: string;
  user_id: number;
  user_name: string | null;
  lesson_id: number;
  lesson_name: string | null;
  time_spent_ms: number;
  completion_pct: number;
  avg_bite_time_ms: number | null;
  reset_bites?: number;
  total_wasted_sec?: number;
}

export interface AnalyticsWasteSummary {
  total_backward_movements: number;
  sessions_with_resets: number;
  unique_students_affected: number;
  total_wasted_hours: number;
  avg_wasted_per_bite_sec: number;
}

export interface AnalyticsPerStudentWaste {
  user_id: number;
  name: string | null;
  reset_bite_attempts: number;
  sessions_with_resets: number;
  wasted_hours: number;
  avg_wasted_per_bite_sec: number;
}

export interface AnalyticsWasteResponse {
  summary: AnalyticsWasteSummary;
  per_student_waste: AnalyticsPerStudentWaste[];
  top_wasted_sessions: AnalyticsWasteRow[];
  daily_waste_trend: {
    date: string;
    waste_hours: number;
    total_hours: number;
    waste_pct: number;
  }[];
  low_completion_sessions: AnalyticsWasteRow[];
}

export interface AnalyticsHeatmapCell {
  user_id: number;
  lesson_id: number;
  avg_completion_pct: number;
}

export interface AnalyticsHeatmapResponse {
  students: { user_id: number; name: string | null }[];
  lessons: { lesson_id: number; name: string | null }[];
  cells: AnalyticsHeatmapCell[];
}

export interface AnalyticsFilterParams {
  dimension?: SessionsDimension;
  start_date?: string;
  end_date?: string;
  classroom_id?: number;
  lesson_id?: number;
  cohort_id?: string;
  user_ids?: number[];
  sort_by?: string;
  sort_dir?: string;
}

export type SessionsDimension = "lesson" | "day" | "week";

export interface SessionsMetricsTimeBreakdown {
  ai_speaking_ms: number;
  user_speaking_ms: number;
  scaffolding_ms: number;
  tech_issues_ms: number;
  input_box_ms: number;
  scratchpad_ms: number;
}

export interface SessionsMetricsScaffolding {
  none: number;
  low: number;
  medium: number;
  high: number;
}

export interface SessionsMetricsSummary {
  total_sessions: number;
  avg_completion_pct: number;
  avg_time_ms: number;
  avg_accuracy_pct: number;
  avg_stt_ms: number;
  avg_tts_ms: number;
  avg_llm_ms: number;
  total_messages: number;
  mastery_pct: number;
}

export interface SessionsMetricsRow {
  key: string;
  label: string;
  sessions: number;
  avg_completion_pct: number;
  avg_time_ms: number;
  accuracy_pct: number;
  correct: number;
  incorrect: number;
  help_requests: number;
  avg_stt_ms: number;
  avg_tts_ms: number;
  avg_llm_ms: number;
  messages: number;
  words: number;
  completed_count: number;
  mastery_pct: number;
  mastered_count: number;
  time_breakdown: SessionsMetricsTimeBreakdown;
  scaffolding: SessionsMetricsScaffolding;
}

export interface SessionsMetricsResponse {
  summary: SessionsMetricsSummary;
  rows: SessionsMetricsRow[];
}

export interface StudentsClassAverages {
  completion_pct: number;
  accuracy_pct: number;
  time_ms: number;
  sessions: number;
  on_track_count: number;
  at_risk_count: number;
  stalled_count: number;
  regressing_count: number;
}

export interface StudentsStudentRow {
  user_id: number;
  name: string | null;
  lessons_started: number;
  lessons_completed: number;
  avg_completion_pct: number;
  completion_deviation: number;
  completion_percentile: number;
  avg_time_ms: number;
  time_deviation: number;
  time_percentile: number;
  accuracy_pct: number;
  accuracy_deviation: number;
  accuracy_percentile: number;
  total_sessions: number;
  scaffolding_pct: number;
  avg_exit_ticket_score?: number | null;
}

export interface StudentsClassTrendRow {
  date: string;
  avg_completion: number;
  at_risk_count: number;
  scaffolding_pct: number;
}

export interface StudentsMetricsResponse {
  class_averages: StudentsClassAverages;
  students: StudentsStudentRow[];
  class_trend: StudentsClassTrendRow[];
}

export interface FirstPassStudentScaffolding {
  user_id: number;
  name: string | null;
  none_pct: number;
  low_pct: number;
  medium_pct: number;
  high_pct: number;
}

export interface FirstPassResponse {
  first_pass_times: {
    user_id: number;
    name: string | null;
    avg_first_pass_time_ms: number;
  }[];
  scaffolding_distribution: FirstPassStudentScaffolding[];
}

// ---------------------------------------------------------------------------
// Granular DDB-native types (Phase 3)
// ---------------------------------------------------------------------------

export interface SlideTimelineEntry {
  slide_index: number;
  slide_start_ts: number;
  slide_end_ts: number;
  time_on_slide_ms: number;
  is_backward: boolean;
  message_count: number;
  user_message_count: number;
  tutor_message_count: number;
}

export interface SessionTimelineResponse {
  session_id: string;
  user_id: number;
  lesson_id: number;
  total_duration_ms: number;
  slides: SlideTimelineEntry[];
}

export interface SlideMessageEntry {
  role: string;
  word_count: number;
}

export interface SlideMessageStats {
  slide_index: number;
  messages: SlideMessageEntry[];
  total_words_user: number;
  total_words_tutor: number;
  avg_response_length_user: number;
  avg_response_length_tutor: number;
}

export interface SessionMessagesResponse {
  session_id: string;
  slides: SlideMessageStats[];
}

export interface SlideDifficultyEntry {
  slide_index: number;
  avg_time_ms: number;
  backward_visit_rate: number;
  avg_messages: number;
}

export interface LessonSlideDifficulty {
  lesson_id: number;
  lesson_name: string | null;
  slides: SlideDifficultyEntry[];
}

export interface SlideDifficultyResponse {
  lessons: LessonSlideDifficulty[];
}

export interface AnalyticsExportRow {
  session_id: string;
  date: string | null;
  student_name: string | null;
  student_email: string | null;
  lesson_id: number | null;
  lesson_name: string | null;
  time_spent_min: number | null;
  completion_pct: number | null;
  lesson_status: string | null;
  max_slide_reached: number | null;
  total_slides: number | null;
  total_attempts: number;
  correct_count: number;
  incorrect_count: number;
  accuracy_pct: number | null;
  help_requests: number;
  scaffolding_rate_pct: number | null;
  exit_ticket_score: number | null;
}

export interface AnalyticsExportResponse {
  rows: AnalyticsExportRow[];
  total: number;
}
